import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState, Money } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Upload, Plus, Pencil, Trash2, Search, Database, TrendingUp,
  CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, History, GitMerge,
  ArrowRight, X, XCircle, Download, Sparkles, ShieldAlert, Tags, Wallet, Loader2,
} from "lucide-react";

const ALL = "__all__";
const MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const PAGE = 25;
const PAY_STATUSES = ["Paid", "Partial", "Unpaid"];

// Map any stored payment status spelling to the canonical Paid / Partial / Unpaid.
const normPayUi = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (["paid", "paid in full", "fully paid"].includes(s)) return "Paid";
  if (["partial", "partially paid"].includes(s)) return "Partial";
  return "Unpaid";
};

function PayBadge({ status, testId }) {
  const s = normPayUi(status);
  const cls =
    s === "Paid" ? "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]"
      : s === "Partial" ? "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] text-[hsl(var(--warning))]"
        : "border-border text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] ${cls}`} data-testid={testId}>{s}</Badge>;
}

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

/* ------------------------------------------------------------------ summary */
function SummaryBar({ summary }) {
  const { money } = useCurrency();
  if (!summary) return <Skeleton className="h-20 rounded-xl" />;
  const tiles = [
    { label: "Total Spend", value: money(summary.spends_total), sub: `${summary.spends_rows} line items`, testid: "dm-sum-spend" },
    { label: "Project Spend", value: money(summary.project_total), sub: "Attributed to projects", testid: "dm-sum-project" },
    { label: "Corporate / Brand", value: money(summary.corporate_total), sub: "Non-project spends", testid: "dm-sum-corp" },
    { label: "Leads (Achieved)", value: (summary.leads_actual || 0).toLocaleString("en-IN"), sub: `${summary.funnel_rows} funnel rows · ${summary.bookings_actual} bookings`, testid: "dm-sum-leads" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="dm-summary">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-xl border bg-card p-3.5" data-testid={t.testid}>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t.label}</p>
          <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{t.value}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{t.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ safe re-import (preview -> commit) */
function ImportPanel({ onImported, canEdit }) {
  const { money } = useCurrency();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [mode, setMode] = useState("spends"); // 'spends' = invoices only | 'full' = spends + funnel
  const [report, setReport] = useState(null);

  const hasFunnelSheet = !!(preview && (preview.funnel_sheet || (preview.after?.funnel_rows || 0) > 0));

  const doPreview = async (file) => {
    if (!file) return;
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.dataImportPreview(fd);
      setPreview(res);
      setFileObj(file);
      // auto-select the safe mode: if the workbook has no funnel sheet, import invoices only
      const funnelPresent = !!(res.funnel_sheet || (res.after?.funnel_rows || 0) > 0);
      setMode(funnelPresent ? "full" : "spends");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read workbook");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const closePreview = () => { setPreview(null); setFileObj(null); };

  const doCommit = async () => {
    if (mode === "spends") {
      if (!fileObj) return;
      setCommitting(true);
      try {
        const fd = new FormData();
        fd.append("file", fileObj);
        const res = await api.dataImportSpends(fd);
        setReport({ ...res.report, mode: "spends" });
        toast.success(`Replaced ${res.report.spends_rows} invoices`, {
          description: "Auto-tagged from the sheet. Leads & targets were left untouched.",
        });
        closePreview();
        onImported?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Import failed");
      } finally { setCommitting(false); }
      return;
    }
    if (!preview?.token) return;
    setCommitting(true);
    try {
      const res = await api.dataImportCommit(preview.token);
      setReport({ ...res.report, mode: "full" });
      toast.success(`Imported ${res.report.spends_rows} spends & ${res.report.funnel_rows} funnel rows`);
      closePreview();
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const hasErrors = (preview?.errors || []).length > 0;

  return (
    <SectionCard title="Import invoices" description="Upload your Spends Categorization .xlsx. Tags (project, vertical, platform, activity, outcome, performance) are auto-applied from the sheet. By default only invoices are replaced — leads & targets stay untouched." testId="dm-import-card">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => doPreview(e.target.files?.[0])} data-testid="dm-import-input" />
          <Button disabled={!canEdit || busy} onClick={() => fileRef.current?.click()} data-testid="dm-import-btn">
            <Upload className="mr-2 h-4 w-4" /> {busy ? "Reading…" : "Choose .xlsx & preview"}
          </Button>
          {!canEdit && <span className="text-xs text-muted-foreground">Only Admin / Marketing Head can import.</span>}
          <span className="text-xs text-muted-foreground">Nothing is replaced until you confirm.</span>
        </div>
        {report && (
          <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-3 text-sm" data-testid="dm-import-report">
            <div className="mb-2 flex items-center gap-2 font-medium text-[hsl(var(--success))]">
              <CheckCircle2 className="h-4 w-4" /> {report.mode === "spends" ? "Invoices imported — leads & targets untouched" : "Import complete"}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-3">
              <span>Invoices: <b className="tabular-nums">{report.spends_rows}</b></span>
              <span>Total: <b className="tabular-nums">₹{(report.spends_total / 1e7).toFixed(2)} Cr</b></span>
              {report.mode === "spends"
                ? <span>Leads / targets: <b className="text-[hsl(var(--success))]">preserved</b></span>
                : <span>Funnel rows: <b className="tabular-nums">{report.funnel_rows}</b></span>}
            </div>
          </div>
        )}
      </div>

      {/* Validation report dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && !committing && closePreview()}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="dm-import-preview-dialog">
          <DialogHeader>
            <DialogTitle>Review import before replacing data</DialogTitle>
            <DialogDescription>
              {mode === "spends"
                ? <>This will <b className="text-foreground">replace all invoices</b> with the sheet below and auto-apply its tags. <b className="text-[hsl(var(--success))]">Leads &amp; targets are kept.</b></>
                : <>This will <b className="text-foreground">replace all spends &amp; funnel data</b>. Review the validation report, then confirm.</>}
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4">
              {/* import mode selector */}
              <div className="rounded-lg border bg-card p-3" data-testid="dm-import-mode">
                <p className="mb-2 text-xs font-medium text-foreground">What should this upload replace?</p>
                <RadioGroup value={mode} onValueChange={setMode} className="gap-2">
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 has-[:checked]:border-primary has-[:checked]:bg-[hsl(var(--primary)/0.05)]" data-testid="dm-import-mode-spends">
                    <RadioGroupItem value="spends" id="mode-spends" className="mt-0.5" />
                    <span className="text-xs">
                      <span className="font-medium text-foreground">Invoices only <Badge variant="secondary" className="ml-1 text-[10px]">Recommended</Badge></span>
                      <span className="mt-0.5 block text-muted-foreground">Replace the {preview.after?.spends_rows ?? "—"} spend line items. Leads, site visits, bookings &amp; targets are left exactly as they are.</span>
                    </span>
                  </label>
                  <label className={`flex items-start gap-2.5 rounded-md border p-2.5 ${hasFunnelSheet ? "cursor-pointer has-[:checked]:border-primary has-[:checked]:bg-[hsl(var(--primary)/0.05)]" : "cursor-not-allowed opacity-50"}`} data-testid="dm-import-mode-full">
                    <RadioGroupItem value="full" id="mode-full" className="mt-0.5" disabled={!hasFunnelSheet} />
                    <span className="text-xs">
                      <span className="font-medium text-foreground">Full workbook (spends + leads/funnel)</span>
                      <span className="mt-0.5 block text-muted-foreground">{hasFunnelSheet ? "Replace both invoices AND the leads/funnel data from this workbook." : "This file has no leads/funnel sheet, so there's nothing to replace on that side."}</span>
                    </span>
                  </label>
                </RadioGroup>
              </div>

              {/* before -> after */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="dm-import-diff">
                <DiffTile label="Spend line items" before={preview.before?.spends_rows} after={preview.after?.spends_rows} />
                <DiffTile label="Total spend" before={money(preview.before?.spends_total)} after={money(preview.after?.spends_total)} />
                {mode === "spends"
                  ? <DiffTile label="Leads / funnel rows" before={preview.before?.funnel_rows} after={preview.before?.funnel_rows} kept />
                  : <DiffTile label="Funnel rows" before={preview.before?.funnel_rows} after={preview.after?.funnel_rows} />}
              </div>

              {/* errors / warnings */}
              {hasErrors && (
                <div className="rounded-lg border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.06)] p-3 text-xs text-[hsl(var(--danger))]" data-testid="dm-import-errors">
                  <div className="mb-1 flex items-center gap-1.5 font-medium"><XCircle className="h-3.5 w-3.5" /> {preview.errors.length} blocking error(s)</div>
                  <ul className="list-disc pl-4">{preview.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              {(preview.warnings || []).length > 0 && (
                <div className="rounded-lg border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.06)] p-3 text-xs text-[hsl(var(--warning))]" data-testid="dm-import-warnings">
                  <div className="mb-1 flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> {preview.warnings.length} warning(s)</div>
                  <ul className="max-h-24 list-disc overflow-y-auto pl-4">{preview.warnings.slice(0, 20).map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}

              {/* new values introduced */}
              {(preview.new_projects?.length > 0 || preview.new_verticals?.length > 0 || preview.new_vendors?.length > 0) && (
                <div className="rounded-lg border bg-card p-3 text-xs" data-testid="dm-import-newvalues">
                  <p className="mb-1.5 font-medium text-foreground">New values that will be added to master lists</p>
                  <NewValueRow label="Projects" items={preview.new_projects} />
                  <NewValueRow label="Verticals" items={preview.new_verticals} />
                  <NewValueRow label="Vendors" items={preview.new_vendors} max={12} />
                </div>
              )}

              {/* row-level issues */}
              <div>
                <p className="mb-1.5 flex items-center gap-2 text-xs font-medium">
                  Row validation
                  <Badge variant={preview.issues_count ? "outline" : "secondary"} className={preview.issues_count ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]" : ""} data-testid="dm-import-issue-count">
                    {preview.issues_count} row(s) flagged
                  </Badge>
                </p>
                {preview.issues_count > 0 ? (
                  <div className="max-h-40 overflow-y-auto rounded-lg border" data-testid="dm-import-issues">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[hsl(var(--surface-2))] text-muted-foreground">
                        <tr><th className="px-2 py-1.5 text-left">Row</th><th className="px-2 py-1.5 text-left">Vendor</th><th className="px-2 py-1.5 text-left">Project</th><th className="px-2 py-1.5 text-left">Problem(s)</th></tr>
                      </thead>
                      <tbody className="divide-y">
                        {preview.issues.map((it, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1 tabular-nums">{it.row}</td>
                            <td className="px-2 py-1">{it.vendor || "—"}</td>
                            <td className="px-2 py-1">{it.project || "—"}</td>
                            <td className="px-2 py-1 text-[hsl(var(--warning))]">{(it.problems || []).join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rounded-lg border bg-[hsl(var(--success)/0.06)] px-3 py-2 text-xs text-[hsl(var(--success))]">All rows passed validation.</p>
                )}
              </div>

              {/* sample preview */}
              <div>
                <p className="mb-1.5 text-xs font-medium">Sample of parsed spends (first {preview.sample?.length || 0})</p>
                <div className="max-h-52 overflow-auto rounded-lg border" data-testid="dm-import-sample">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[hsl(var(--surface-2))] text-muted-foreground">
                      <tr>{["Date", "Project", "Type", "Vertical", "Platform", "Activity", "Vendor", "Amount"].map((h) => <th key={h} className={`px-2 py-1.5 ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y">
                      {(preview.sample || []).map((s, i) => (
                        <tr key={i}>
                          <td className="whitespace-nowrap px-2 py-1">{s.date || "—"}</td>
                          <td className="px-2 py-1">{s.project || "—"}</td>
                          <td className="px-2 py-1">{s.spend_type}</td>
                          <td className="px-2 py-1">{s.vertical || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.platform || "—"}</td>
                          <td className="max-w-[160px] truncate px-2 py-1 text-muted-foreground" title={s.activity}>{s.activity || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.vendor || "—"}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{money(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closePreview} disabled={committing} data-testid="dm-import-cancel">Cancel</Button>
            <Button onClick={doCommit} disabled={committing || hasErrors}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]" data-testid="dm-import-confirm">
              {committing ? "Replacing…" : (mode === "spends" ? "Confirm & Replace invoices" : "Confirm & Replace all data")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function DiffTile({ label, before, after, kept }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      {kept ? (
        <div className="mt-1 flex items-center gap-2 text-sm">
          <span className="font-heading font-semibold tabular-nums">{before ?? "—"}</span>
          <Badge variant="secondary" className="text-[10px] text-[hsl(var(--success))]">kept</Badge>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2 text-sm">
          <span className="tabular-nums text-muted-foreground line-through">{before ?? "—"}</span>
          <ArrowRight className="h-3.5 w-3.5 text-primary" />
          <span className="font-heading font-semibold tabular-nums">{after ?? "—"}</span>
        </div>
      )}
    </div>
  );
}

function NewValueRow({ label, items, max = 20 }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-1 flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground">{label}:</span>
      {items.slice(0, max).map((v) => <Badge key={v} variant="secondary" className="text-[10px]">{v}</Badge>)}
      {items.length > max && <span className="text-muted-foreground">+{items.length - max} more</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ change history dialog */
function HistoryDialog({ open, onOpenChange, entityType, row }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (open && row?.id) {
      setRows(null);
      api.audit({ entity: entityType, entity_id: row.id, limit: 100 }).then(setRows).catch(() => setRows([]));
    }
  }, [open, row, entityType]);

  const actionColor = (a) => a === "create" ? "text-[hsl(var(--success))]" : a === "delete" ? "text-[hsl(var(--danger))]" : "text-primary";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto" data-testid="dm-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Change history</DialogTitle>
          <DialogDescription>{entityType === "Funnel" ? `${row?.project} · ${row?.month} · ${row?.vertical}` : `${row?.vendor || "—"} · ${row?.project || "—"}`}</DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <Skeleton className="h-32 w-full" />
        ) : rows.length === 0 ? (
          <EmptyState title="No manual changes yet" message="This row came from the imported workbook and hasn't been edited by anyone." />
        ) : (
          <ol className="relative space-y-3 border-l pl-4" data-testid="dm-history-list">
            {rows.map((e, i) => (
              <li key={i} className="relative" data-testid="dm-history-item">
                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary" />
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-medium capitalize ${actionColor(e.action)}`}>{e.action}</span>
                  <span className="text-[11px] text-muted-foreground">{fmtDateTime(e.changed_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground">by {e.changed_by || e.changed_by_email || "system"}</p>
                {(e.changes || []).length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {e.changes.map((ch, j) => (
                      <div key={j} className="flex flex-wrap items-center gap-1 text-[11px]">
                        <span className="rounded bg-secondary px-1.5 py-0.5 font-medium">{ch.field}</span>
                        <span className="text-muted-foreground line-through">{String(ch.old ?? "—")}</span>
                        <ArrowRight className="h-3 w-3 text-primary" />
                        <span className="font-medium">{String(ch.new ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                )}
                {e.reason && <p className="mt-0.5 text-[11px] italic text-muted-foreground">{e.reason}</p>}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ vendor merge dialog */
function MergeVendorsDialog({ open, onOpenChange, onMerged }) {
  const { money } = useCurrency();
  const [vendors, setVendors] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [q, setQ] = useState("");
  const [sources, setSources] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSources([]); setTarget(""); setQ(""); setVendors(null); setSuggestions(null);
      api.dataVendors().then(setVendors).catch(() => setVendors([]));
      api.dataVendorSuggestions().then(setSuggestions).catch(() => setSuggestions([]));
    }
  }, [open]);

  const loadCluster = (cl) => {
    setSources(cl.variants.map((v) => v.vendor));
    setTarget(cl.canonical);
  };

  const filtered = useMemo(() => {
    if (!vendors) return [];
    const ql = q.trim().toLowerCase();
    return ql ? vendors.filter((v) => v.vendor.toLowerCase().includes(ql)) : vendors;
  }, [vendors, q]);

  const toggle = (name) => setSources((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const affected = useMemo(() => (vendors || []).filter((v) => sources.includes(v.vendor)).reduce((a, v) => a + v.count, 0), [vendors, sources]);

  const merge = async () => {
    const t = target.trim();
    if (sources.length === 0) { toast.error("Pick at least one vendor to merge"); return; }
    if (!t) { toast.error("Enter the target vendor name"); return; }
    setBusy(true);
    try {
      const res = await api.dataMergeVendors({ sources, target: t });
      toast.success(`Reassigned ${res.modified} row(s) to "${res.target}"`);
      onMerged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="dm-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge className="h-4 w-4" /> Merge vendors</DialogTitle>
          <DialogDescription>Select duplicate spellings, then set the single name they should all become.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {suggestions && suggestions.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-[hsl(var(--primary)/0.05)] p-2.5" data-testid="dm-merge-suggestions">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> {suggestions.length} likely duplicate group(s) detected
              </p>
              <div className="max-h-40 space-y-1.5 overflow-y-auto pr-1">
                {suggestions.map((cl, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5" data-testid="dm-merge-suggestion">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1">
                        {cl.variants.map((v) => (
                          <span key={v.vendor} className={`truncate rounded px-1.5 py-0.5 text-[11px] ${v.vendor === cl.canonical ? "bg-primary/15 font-medium text-foreground" : "bg-secondary text-muted-foreground"}`} title={`${v.vendor} · ${v.count} rows`}>{v.vendor}</span>
                        ))}
                      </div>
                      <p className="mt-0.5 text-[10px] text-muted-foreground">{cl.total_rows} rows · {money(cl.total_amount)} → “{cl.canonical}”</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 shrink-0 text-[11px]" onClick={() => loadCluster(cl)} data-testid="dm-merge-suggestion-use">Use</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…" className="h-9 pl-8" data-testid="dm-merge-search" />
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border" data-testid="dm-merge-list">
            {vendors === null ? (
              <div className="p-3"><Skeleton className="h-32 w-full" /></div>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">No vendors match.</p>
            ) : filtered.map((v) => (
              <label key={v.vendor} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-[hsl(var(--surface-2))]" data-testid="dm-merge-vendor-row">
                <Checkbox checked={sources.includes(v.vendor)} onCheckedChange={() => toggle(v.vendor)} data-testid={`dm-merge-pick`} />
                <span className="flex-1 truncate" title={v.vendor}>{v.vendor}</span>
                <span className="text-[11px] tabular-nums text-muted-foreground">{v.count} · {money(v.total)}</span>
                <button type="button" onClick={() => setTarget(v.vendor)} className="text-[11px] text-primary hover:underline" title="Use as target name">use →</button>
              </label>
            ))}
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Target vendor name (all selected become this)</Label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 5 Star Advertising" className="mt-1 h-9" data-testid="dm-merge-target" />
          </div>

          {sources.length > 0 && (
            <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-2.5 text-xs" data-testid="dm-merge-summary">
              <b className="tabular-nums">{affected}</b> row(s) across <b>{sources.length}</b> vendor(s) will become
              <b className="text-foreground"> “{target.trim() || "…"}”</b>.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={merge} disabled={busy || sources.length === 0 || !target.trim()} data-testid="dm-merge-confirm">
            {busy ? "Merging…" : "Merge vendors"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ editor dialog */
function RecordDialog({ open, onOpenChange, title, fields, initial, onSave, testPrefix }) {
  const [form, setForm] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial || {}); }, [initial, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await onSave(form); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription>Edit values and save. Changes are audited.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <div key={f.key} className={f.full ? "col-span-2" : ""}>
              <Label className="text-xs">{f.label}</Label>
              {f.type === "select" ? (
                <Select value={form[f.key] ?? ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger className="h-9" data-testid={`${testPrefix}-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                  <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)}
                  className="h-9" data-testid={`${testPrefix}-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid={`${testPrefix}-save`}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ bulk edit bar */
function BulkEditBar({ count, fields, onApply, onClear, canDelete, onDelete }) {
  const [field, setField] = useState("vertical");
  const [value, setValue] = useState("");
  const active = fields.find((f) => f.key === field) || fields[0];
  const onFieldChange = (f) => { setField(f); setValue(""); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-[hsl(var(--primary)/0.06)] p-2.5" data-testid="dm-bulk-bar">
      <Badge className="bg-primary text-primary-foreground" data-testid="dm-bulk-count">{count} selected</Badge>
      <span className="text-xs text-muted-foreground">Set</span>
      <Select value={field} onValueChange={onFieldChange}>
        <SelectTrigger className="h-8 w-[150px]" data-testid="dm-bulk-field"><SelectValue /></SelectTrigger>
        <SelectContent>{fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-xs text-muted-foreground">to</span>
      {active.type === "select" ? (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger className="h-8 w-[190px]" data-testid="dm-bulk-value"><SelectValue placeholder={`Choose ${active.label.toLowerCase()}…`} /></SelectTrigger>
          <SelectContent className="max-h-72">{(active.opts || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={`New ${active.label.toLowerCase()}…`} className="h-8 w-[190px]" data-testid="dm-bulk-value" />
      )}
      <Button size="sm" className="h-8" disabled={!value} onClick={() => onApply(field, value)} data-testid="dm-bulk-apply">Apply to {count}</Button>
      {canDelete && (
        <>
          <span className="mx-0.5 h-5 w-px bg-border" />
          <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--danger)/0.5)] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.08)]" onClick={onDelete} data-testid="dm-bulk-delete">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete {count}
          </Button>
        </>
      )}
      <Button size="sm" variant="ghost" className="h-8" onClick={onClear} data-testid="dm-bulk-clear"><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>
    </div>
  );
}

/* ------------------------------------------------------------------ spends panel */
function SpendsPanel({ lists, canEdit, canDelete, onChanged, reloadToken }) {
  const { money } = useCurrency();
  const [data, setData] = useState({ rows: [], total: 0, sum: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [project, setProject] = useState(ALL);
  const [vertical, setVertical] = useState(ALL);
  const [month, setMonth] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [page, setPage] = useState(0);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [history, setHistory] = useState(null);
  const [merge, setMerge] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDel, setBulkDel] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: PAGE, offset: page * PAGE };
    if (q) params.q = q;
    if (project !== ALL) params.project = project;
    if (vertical !== ALL) params.vertical = vertical;
    if (month !== ALL) params.month = month;
    if (type !== ALL) params.spend_type = type;
    const res = await api.dataListSpends(params);
    setData(res); setLoading(false);
  }, [q, project, vertical, month, type, page, reloadToken]);
  useEffect(() => { load(); }, [load]);
  // reset selection whenever the visible set changes
  useEffect(() => { setSelected(new Set()); }, [q, project, vertical, month, type, page]);

  const projects = (lists?.projects || []).map((p) => p.name);
  const verticals = lists?.verticals || [];
  const fields = [
    { key: "date", label: "Date", type: "date" },
    { key: "spend_month", label: "Month", type: "select", options: MONTHS },
    { key: "project", label: "Project", type: "select", options: projects },
    { key: "spend_type", label: "Type", type: "select", options: ["Project", "Corporate"] },
    { key: "vertical", label: "Vertical", type: "select", options: verticals },
    { key: "platform", label: "Platform" },
    { key: "cost_type", label: "Cost / Dept" },
    { key: "vis_perf", label: "Vis / Perf", type: "select", options: ["Visibility", "Performance"] },
    { key: "activity", label: "Activity", full: true },
    { key: "outcome", label: "Outcome" },
    { key: "vendor", label: "Vendor" },
    { key: "payment_status", label: "Payment Status", type: "select", options: PAY_STATUSES },
    { key: "amount", label: "Amount (₹)", type: "number" },
  ];
  const bulkFields = [
    { key: "payment_status", label: "Payment Status", type: "select", opts: PAY_STATUSES },
    { key: "vertical", label: "Vertical", type: "select", opts: verticals },
    { key: "project", label: "Project", type: "select", opts: projects },
    { key: "spend_type", label: "Type", type: "select", opts: ["Project", "Corporate"] },
    { key: "vis_perf", label: "Vis / Perf", type: "select", opts: ["Visibility", "Performance"] },
    { key: "vendor", label: "Vendor", type: "text" },
    { key: "platform", label: "Platform", type: "text" },
    { key: "cost_type", label: "Cost / Dept", type: "text" },
    { key: "activity", label: "Activity", type: "text" },
    { key: "outcome", label: "Outcome", type: "text" },
  ];

  const saveEdit = async (form) => {
    if (form.id) await api.dataUpdateSpend(form.id, form);
    else await api.dataCreateSpend(form);
    toast.success("Saved"); load(); onChanged?.();
  };
  const doDelete = async () => {
    await api.dataDeleteSpend(del.id); toast.success("Deleted"); setDel(null); load(); onChanged?.();
  };

  const pageIds = data.rows.map((r) => r.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const filterParams = () => {
    const p = {};
    if (q) p.q = q;
    if (project !== ALL) p.project = project;
    if (vertical !== ALL) p.vertical = vertical;
    if (month !== ALL) p.month = month;
    if (type !== ALL) p.spend_type = type;
    return p;
  };
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const res = await api.dataSpendIds(filterParams());
      setSelected(new Set(res.ids || []));
      toast.success(`Selected all ${res.total} matching invoice(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not select all invoices");
    } finally { setSelectingAll(false); }
  };

  const applyBulk = async (field, value) => {
    const ids = [...selected];
    try {
      const res = await api.dataBulkUpdateSpends({ ids, updates: { [field]: value } });
      toast.success(`Updated ${res.modified} row(s)`);
      setSelected(new Set());
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  const doBulkDelete = async () => {
    const ids = [...selected];
    try {
      const res = await api.dataBulkDeleteSpends({ ids });
      toast.success(`Deleted ${res.deleted} row(s)`);
      setBulkDel(false); setSelected(new Set());
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };

  const pages = Math.ceil((data.total || 0) / PAGE);

  return (
    <div className="space-y-3" data-testid="dm-spends-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search vendor / activity…" value={q} onChange={(e) => { setPage(0); setQ(e.target.value); }}
            className="h-9 w-[220px] pl-8" data-testid="dm-spends-search" />
        </div>
        <FilterSelect value={project} onChange={(v) => { setPage(0); setProject(v); }} options={projects} label="All projects" testid="dm-spends-filter-project" />
        <FilterSelect value={vertical} onChange={(v) => { setPage(0); setVertical(v); }} options={verticals} label="All verticals" testid="dm-spends-filter-vertical" />
        <FilterSelect value={month} onChange={(v) => { setPage(0); setMonth(v); }} options={MONTHS} label="All months" testid="dm-spends-filter-month" />
        <FilterSelect value={type} onChange={(v) => { setPage(0); setType(v); }} options={["Project", "Corporate"]} label="All types" testid="dm-spends-filter-type" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{data.total} rows · <b className="tabular-nums">{money(data.sum)}</b></span>
          {canEdit && <Button size="sm" variant="outline" onClick={() => setMerge(true)} data-testid="dm-merge-open"><GitMerge className="mr-1.5 h-3.5 w-3.5" /> Merge vendors</Button>}
          {canEdit && <Button size="sm" onClick={() => setEdit({})} data-testid="dm-spends-add"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add</Button>}
        </div>
      </div>

      {canEdit && selected.size > 0 && (
        <BulkEditBar count={selected.size} fields={bulkFields} onApply={applyBulk}
          onClear={() => setSelected(new Set())} canDelete={canDelete} onDelete={() => setBulkDel(true)} />
      )}

      {canEdit && allOnPageSelected && data.total > pageIds.length && (
        <div className="flex flex-wrap items-center justify-center gap-2 rounded-lg border border-primary/30 bg-[hsl(var(--primary)/0.06)] px-3 py-2 text-xs" data-testid="dm-select-all-banner">
          {selected.size >= data.total ? (
            <>
              <span>All <b className="tabular-nums">{data.total}</b> matching invoice(s) selected.</span>
              <Button size="sm" variant="link" className="h-auto p-0 text-xs" onClick={() => setSelected(new Set())} data-testid="dm-clear-all-matching">Clear selection</Button>
            </>
          ) : (
            <>
              <span>All <b className="tabular-nums">{pageIds.length}</b> on this page selected.</span>
              <Button size="sm" variant="link" className="h-auto p-0 text-xs font-medium" onClick={selectAllMatching} disabled={selectingAll} data-testid="dm-select-all-matching">
                {selectingAll ? "Selecting…" : `Select all ${data.total} matching invoices`}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="rounded-xl border">
        <div className="table-scroll" style={{ maxHeight: 460 }}>
          <table className="w-full text-sm" data-testid="dm-spends-table">
            <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {canEdit && (
                  <th className="th-sticky th-sticky-left px-2.5 py-2 text-left font-medium">
                    <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} aria-label="Select all on page" data-testid="dm-select-all" />
                  </th>
                )}
                {["Date", "Month", "Project", "Type", "Vertical", "Platform", "Activity", "Vis/Perf", "Vendor", "Payment", "Amount", ""].map((h, i) => (
                  <th key={i} className={`th-sticky px-2.5 py-2 ${h === "Amount" ? "text-right" : "text-left"} font-medium`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={canEdit ? 13 : 12} className="p-6"><Skeleton className="h-40 w-full" /></td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={canEdit ? 13 : 12} className="p-6"><EmptyState title="No spends" message="Adjust filters or import a workbook." /></td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.id} className={`hover:bg-[hsl(var(--surface-2))] ${selected.has(r.id) ? "bg-[hsl(var(--primary)/0.06)]" : ""}`} data-testid="dm-spend-row">
                  {canEdit && (
                    <td className="td-sticky-left px-2.5 py-1.5">
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} aria-label="Select row" data-testid={`dm-select-${r.id}`} />
                    </td>
                  )}
                  <td className="px-2.5 py-1.5 text-xs whitespace-nowrap">{r.date || "—"}</td>
                  <td className="px-2.5 py-1.5 text-xs">{r.spend_month || "—"}</td>
                  <td className="px-2.5 py-1.5 font-medium">{r.project}</td>
                  <td className="px-2.5 py-1.5"><Badge variant="outline" className="text-[10px]">{r.spend_type}</Badge></td>
                  <td className="px-2.5 py-1.5 text-xs">{r.vertical || "—"}</td>
                  <td className="px-2.5 py-1.5 text-xs text-muted-foreground">{r.platform || "—"}</td>
                  <td className="px-2.5 py-1.5 text-xs text-muted-foreground max-w-[200px] truncate" title={r.activity}>{r.activity || "—"}</td>
                  <td className="px-2.5 py-1.5 text-xs">{r.vis_perf}</td>
                  <td className="px-2.5 py-1.5 text-xs text-muted-foreground">{r.vendor || "—"}</td>
                  <td className="px-2.5 py-1.5"><PayBadge status={r.payment_status} testId={`dm-spend-pay-${r.id}`} /></td>
                  <td className="px-2.5 py-1.5 text-right num font-medium"><Money value={r.amount} /></td>
                  <td className="px-2.5 py-1.5 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => setHistory(r)} title="Change history" data-testid={`dm-spend-history-${r.id}`}><History className="h-3.5 w-3.5" /></Button>
                    {canEdit && <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => setEdit(r)} data-testid={`dm-spend-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {canDelete && <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[hsl(var(--danger))]" onClick={() => setDel(r)} data-testid={`dm-spend-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span>Page {page + 1} / {pages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="dm-spends-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid="dm-spends-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      <RecordDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}
        title={edit?.id ? "Edit spend" : "Add spend"} fields={fields} initial={edit} onSave={saveEdit} testPrefix="dm-spend-form" />
      <HistoryDialog open={!!history} onOpenChange={(o) => !o && setHistory(null)} entityType="Spend" row={history} />
      <MergeVendorsDialog open={merge} onOpenChange={setMerge} onMerged={() => { load(); onChanged?.(); }} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="dm-spend-delete-confirm">
          <AlertDialogHeader><AlertDialogTitle>Delete this spend?</AlertDialogTitle>
            <AlertDialogDescription>{del?.vendor} · {money(del?.amount || 0)} — this cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="dm-spend-delete-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent data-testid="dm-bulk-delete-confirm">
          <AlertDialogHeader><AlertDialogTitle>Delete {selected.size} selected spend(s)?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {selected.size} line item(s) and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkDelete} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]" data-testid="dm-bulk-delete-yes">Delete {selected.size}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ------------------------------------------------------------------ funnel panel */
function FunnelPanel({ lists, canEdit, canDelete, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(ALL);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [history, setHistory] = useState(null);
  const uploadRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (project !== ALL) params.project = project;
    const res = await api.dataListFunnel(params);
    setRows(res.rows || []); setLoading(false);
  }, [project]);
  useEffect(() => { load(); }, [load]);

  const projects = (lists?.projects || []).map((p) => p.name);
  const fields = [
    { key: "project", label: "Project", type: "select", options: projects },
    { key: "quarter", label: "Quarter", type: "select", options: ["Q1", "Q2", "Q3", "Q4"] },
    { key: "month", label: "Month", type: "select", options: MONTHS },
    { key: "vertical", label: "Vertical", type: "select", options: ["ATL/BTL", "CX", "Digital"] },
    { key: "leads_target", label: "Leads Target", type: "number" },
    { key: "leads_actual", label: "Leads Achieved", type: "number" },
    { key: "sv_target", label: "SV Target", type: "number" },
    { key: "sv_actual", label: "SV Achieved", type: "number" },
    { key: "booking_target", label: "Booking Target", type: "number" },
    { key: "booking_actual", label: "Booking Achieved", type: "number" },
    { key: "cancellations", label: "Cancellations", type: "number" },
    { key: "final_count", label: "Final Count", type: "number" },
    { key: "revenue_cr_actual", label: "Net Rev (Cr)", type: "number" },
  ];
  const saveEdit = async (form) => {
    if (form.id) await api.dataUpdateFunnel(form.id, form);
    else await api.dataCreateFunnel(form);
    toast.success("Saved · analytics rebuilt"); load(); onChanged?.();
  };
  const doDelete = async () => {
    await api.dataDeleteFunnel(del.id); toast.success("Deleted · analytics rebuilt"); setDel(null); load(); onChanged?.();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.dataFunnelTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "funnel_upload_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Funnel template downloaded");
    } catch (e) { toast.error("Could not download template"); }
  };
  const onUploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error("Please choose an .xlsx file"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.dataImportFunnel(fd);
      const r = res.report || {};
      toast.success(`Funnel updated · ${r.inserted} new, ${r.updated} updated · invoices untouched`);
      load(); onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Funnel upload failed");
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="dm-funnel-panel">
      <input ref={uploadRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onUploadFile} data-testid="dm-funnel-upload-input" />
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect value={project} onChange={setProject} options={projects} label="All projects" testid="dm-funnel-filter-project" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{rows.length} rows · uploads never touch invoices</span>
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="dm-funnel-template-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> Template</Button>
          {canEdit && <Button size="sm" variant="outline" disabled={uploading} onClick={() => uploadRef.current?.click()} data-testid="dm-funnel-upload-btn"><Upload className="mr-1.5 h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}</Button>}
          {canEdit && <Button size="sm" onClick={() => setEdit({ vertical: "Digital" })} data-testid="dm-funnel-add"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add</Button>}
        </div>
      </div>
      <div className="rounded-xl border">
        <div className="table-scroll" style={{ maxHeight: 480 }}>
          <table className="w-full text-sm" data-testid="dm-funnel-table">
            <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {["Project", "Q", "Month", "Vertical", "Leads T", "Leads A", "SV T", "SV A", "Bk T", "Bk A", "Cancel", "Final", "Rev Cr", ""].map((h, i) => (
                  <th key={i} className={`th-sticky px-2 py-2 ${i >= 4 && i <= 12 ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""} font-medium`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={14} className="p-6"><Skeleton className="h-40 w-full" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={14} className="p-6"><EmptyState title="No funnel rows" message="Import a workbook or add a row." /></td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="hover:bg-[hsl(var(--surface-2))]" data-testid="dm-funnel-row">
                  <td className="td-sticky-left px-2 py-1.5 font-medium whitespace-nowrap">{r.project}</td>
                  <td className="px-2 py-1.5 text-xs">{r.quarter}</td>
                  <td className="px-2 py-1.5 text-xs">{r.month}</td>
                  <td className="px-2 py-1.5 text-xs">{r.vertical}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(r.leads_target)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">{Math.round(r.leads_actual)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(r.sv_target)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">{Math.round(r.sv_actual)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(r.booking_target)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">{Math.round(r.booking_actual)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{Math.round(r.cancellations)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Math.round(r.final_count)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{Number(r.revenue_cr_actual).toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => setHistory(r)} title="Change history" data-testid={`dm-funnel-history-${r.id}`}><History className="h-3.5 w-3.5" /></Button>
                    {canEdit && <Button variant="ghost" size="sm" className="h-7 px-1.5" onClick={() => setEdit(r)} data-testid={`dm-funnel-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {canDelete && <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[hsl(var(--danger))]" onClick={() => setDel(r)} data-testid={`dm-funnel-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <RecordDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}
        title={edit?.id ? "Edit funnel row" : "Add funnel row"} fields={fields} initial={edit} onSave={saveEdit} testPrefix="dm-funnel-form" />
      <HistoryDialog open={!!history} onOpenChange={(o) => !o && setHistory(null)} entityType="Funnel" row={history} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="dm-funnel-delete-confirm">
          <AlertDialogHeader><AlertDialogTitle>Delete this funnel row?</AlertDialogTitle>
            <AlertDialogDescription>{del?.project} · {del?.month} · {del?.vertical} — analytics will be rebuilt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="dm-funnel-delete-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ value, onChange, options, label, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[160px]" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={ALL}>{label}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/* ------------------------------------------------------------------ danger zone: purge all spends */
function DangerZone({ summary, onPurged }) {
  const { money } = useCurrency();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = summary?.spends_rows ?? 0;
  const canConfirm = confirmText.trim().toUpperCase() === "DELETE";

  const purge = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      const res = await api.dataPurgeAllSpends();
      toast.success(`Deleted all ${res.deleted} spend line item(s)`, {
        description: "The ledger is now empty. Upload a fresh workbook above to load new data — funnel data was left untouched.",
      });
      setOpen(false); setConfirmText("");
      onPurged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete spends");
    } finally { setBusy(false); }
  };

  return (
    <SectionCard
      title="Danger zone"
      description="Irreversible maintenance actions. Use these before importing a brand-new dataset."
      testId="dm-danger-card"
      className="border-[hsl(var(--danger)/0.35)]">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]">
            <ShieldAlert className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">Delete all invoices / spends</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Permanently removes every spend line item (<b className="tabular-nums text-foreground">{rows}</b> rows · {money(summary?.spends_total || 0)}).
              Funnel &amp; targets are <b>not</b> affected. This cannot be undone.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          className="shrink-0 border-[hsl(var(--danger)/0.5)] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.08)]"
          disabled={rows === 0}
          onClick={() => setOpen(true)}
          data-testid="dm-purge-open">
          <Trash2 className="mr-1.5 h-4 w-4" /> Delete all spends
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) setConfirmText(""); } }}>
        <AlertDialogContent data-testid="dm-purge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[hsl(var(--danger))]">
              <ShieldAlert className="h-5 w-5" /> Delete ALL spends?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all <b className="tabular-nums">{rows}</b> spend line item(s) totalling {money(summary?.spends_total || 0)}.
              Funnel data and targets are untouched. To confirm, type <b className="text-foreground">DELETE</b> below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="h-9"
            autoFocus
            data-testid="dm-purge-input" />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="dm-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); purge(); }}
              disabled={!canConfirm || busy}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]"
              data-testid="dm-purge-yes">
              {busy ? "Deleting…" : "Delete all spends"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ root */
/* -------------------------------------------------- budget allocation import */
function BudgetAllocationImport({ canEdit }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error("Please choose an .xlsx workbook"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.dataImportBudgetAllocation(fd);
      setReport(res.report);
      toast.success(`Loaded ${res.report.count} project budgets · dashboard updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Budget Allocation import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <SectionCard
      title="Budget Allocation"
      description="Load 'Budget Allocation.xlsx' as the source of truth for the editable Budget Allocation planner and the dashboard's Budget & Spend Health (Planned = Budget % × Sale Value). This only updates budgets — invoices and leads/funnel data are never touched."
      testId="dm-ba-card"
      action={canEdit && (
        <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="dm-ba-import-btn">
          <Upload className="mr-1.5 h-3.5 w-3.5" /> {busy ? "Loading…" : "Upload .xlsx"}
        </Button>
      )}
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="dm-ba-file-input" />
      <div className="p-4">
        {!report ? (
          <div className="flex items-start gap-2.5 rounded-lg border border-dashed bg-muted/30 p-3.5 text-sm text-muted-foreground" data-testid="dm-ba-empty">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
            <span>Safe budget-only import. Upload the Budget Allocation matrix workbook — spends and funnel stay intact.</span>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dm-ba-report">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border bg-card p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Projects loaded</p>
                <p className="mt-1 font-heading text-lg font-semibold tabular-nums" data-testid="dm-ba-count">{report.count}</p>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Planned budget</p>
                <p className="mt-1 font-heading text-lg font-semibold tabular-nums" data-testid="dm-ba-estimate">₹{Number(report.estimate_total_cr || 0).toFixed(2)} Cr</p>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Monthly rows</p>
                <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{report.budget_monthly_rows}</p>
              </div>
              <div className="rounded-lg border bg-[hsl(var(--success)/0.08)] p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Data safety</p>
                <p className="mt-1 flex items-center gap-1 text-sm font-medium text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" /> Spends &amp; funnel intact</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="dm-ba-projects">
              {(report.projects_loaded || []).map((p) => (
                <Badge key={p} variant="secondary" className="text-[11px]">{p}</Badge>
              ))}
            </div>
            {(report.unmatched_projects || []).length > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] p-3 text-sm" data-testid="dm-ba-unmatched">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
                <span>Loaded but not recognised as existing projects (no invoices/curated match): <b>{report.unmatched_projects.join(", ")}</b>. Verify the project name or add it to the portfolio.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/* -------------------------------------------------- tag audit (pre-live QA) */
const AUDIT_FLAG_LABEL = {
  missing_project: "Missing project",
  missing_vertical: "Missing vertical",
  missing_platform: "Missing platform",
  zero_amount: "Amount 0",
  unmapped_platform: "Unmapped platform",
  other_bucket: "'Other' bucket",
};

function TagAuditPanel({ reloadToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.tagAudit()); }
    catch { toast.error("Could not load tag audit"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, reloadToken]);

  if (loading && !data) return <Skeleton className="h-64 rounded-xl" />;
  if (!data) return null;
  const ready = data.ready;

  return (
    <div className="space-y-4" data-testid="dm-tag-audit-panel">
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${ready ? "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.06)]" : "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.06)]"}`}>
        <div className="flex items-center gap-3">
          {ready
            ? <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" />
            : <AlertTriangle className="h-6 w-6 text-[hsl(var(--warning))]" />}
          <div>
            <p className="font-heading text-base font-semibold" data-testid="dm-audit-status">
              {ready ? "All invoices are clean — ready to go live" : `${data.flagged} invoice(s) need attention`}
            </p>
            <p className="text-xs text-muted-foreground">{data.clean} of {data.total} invoices pass every tagging check.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="dm-audit-refresh">Re-run audit</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="dm-audit-summary">
        {data.summary.map((s) => (
          <div key={s.key} className={`rounded-xl border p-3 ${s.count > 0 ? "bg-[hsl(var(--danger)/0.05)]" : "bg-card"}`} data-testid={`dm-audit-flag-${s.key}`}>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{s.label}</p>
            <p className={`mt-1 font-heading text-xl font-semibold tabular-nums ${s.count > 0 ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`}>{s.count}</p>
          </div>
        ))}
      </div>

      {data.flagged === 0 ? (
        <EmptyState title="No tagging issues" message="Every invoice has a project, vertical, mapped platform, non-zero amount and a real investment bucket." />
      ) : (
        <div className="rounded-xl border">
          <div className="table-scroll" style={{ maxHeight: 480 }}>
            <table className="w-full text-sm" data-testid="dm-audit-table">
              <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  {["Project", "Vertical", "Platform", "Vendor", "Month", "Amount", "Issues"].map((h, i) => (
                    <th key={h} className={`th-sticky px-2.5 py-2 ${i === 5 ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""} font-medium`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.issues.map((it) => (
                  <tr key={it.id} className="border-t hover:bg-muted/40" data-testid={`dm-audit-row-${it.id}`}>
                    <td className="px-2.5 py-1.5">{it.project || <span className="text-[hsl(var(--danger))]">—</span>}</td>
                    <td className="px-2.5 py-1.5">{it.vertical || <span className="text-[hsl(var(--danger))]">—</span>}</td>
                    <td className="px-2.5 py-1.5">{it.platform || <span className="text-[hsl(var(--danger))]">—</span>}</td>
                    <td className="px-2.5 py-1.5 text-muted-foreground">{it.vendor || "—"}</td>
                    <td className="px-2.5 py-1.5 text-muted-foreground">{it.month || "—"}</td>
                    <td className="px-2.5 py-1.5 text-right num font-medium"><Money value={it.amount} /></td>
                    <td className="px-2.5 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {it.flags.map((f) => (
                          <Badge key={f} variant="outline" className="border-[hsl(var(--danger)/0.4)] text-[10px] text-[hsl(var(--danger))]">{AUDIT_FLAG_LABEL[f] || f}</Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- taxonomy manager (PART 1) */
function TaxonomyManager({ canEdit, onChanged }) {
  const [tax, setTax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("Project");
  const [newLabel, setNewLabel] = useState("");
  const [newEntity, setNewEntity] = useState("");
  const [reassign, setReassign] = useState(null); // { name, count, options, message }
  const [reassignTo, setReassignTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setTax(await api.getSpendTaxonomy()); }
    catch { toast.error("Could not load taxonomy"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = tax?.vertical_invoice_counts || {};
  const labels = tax?.vertical_labels || {};

  const addVertical = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Enter a vertical name"); return; }
    setBusy(true);
    try {
      await api.addVertical({ name, spend_type: newType, label: newLabel.trim() || name });
      toast.success(`Added "${name}" under ${newType}`);
      setNewName(""); setNewLabel("");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add vertical");
    } finally { setBusy(false); }
  };

  const tryDelete = async (name, target = null) => {
    setBusy(true);
    try {
      const res = await api.removeVertical(name, target);
      toast.success(target
        ? `Removed "${name}" · reassigned ${res.reassigned} invoice(s) to "${target}"`
        : `Removed "${name}"`);
      setReassign(null); setReassignTo("");
      await load(); onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && detail.code === "reassign_required") {
        setReassign({ name, count: detail.count, options: detail.options || [], message: detail.message });
        setReassignTo("");
      } else {
        toast.error((detail && detail.message) || detail || "Could not remove vertical");
      }
    } finally { setBusy(false); }
  };

  const saveEntities = async (list, verb, name) => {
    setBusy(true);
    try {
      await api.saveSpendTaxonomy({ corporate_entities: list });
      toast.success(`${verb} entity "${name}"`);
      setNewEntity("");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update entities"); }
    finally { setBusy(false); }
  };
  const addEntity = () => {
    const name = newEntity.trim();
    if (!name) return;
    saveEntities(Array.from(new Set([...(tax?.corporate_entities || []), name])), "Added", name);
  };
  const removeEntity = (name) => saveEntities((tax?.corporate_entities || []).filter((x) => x !== name), "Removed", name);

  const VerticalChips = ({ list, kind }) => (
    <div className="flex flex-wrap gap-1.5" data-testid={`dm-tax-${kind}-list`}>
      {(list || []).length === 0 && <span className="text-xs text-muted-foreground">No verticals yet.</span>}
      {(list || []).map((v) => (
        <span key={v} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs" data-testid={`dm-tax-vertical-${v}`}>
          <span className="font-medium">{labels[v] || v}</span>
          <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">{counts[v] || 0}</span>
          {canEdit && (
            <button type="button" disabled={busy}
              className="text-muted-foreground transition-colors duration-150 hover:text-[hsl(var(--danger))] disabled:opacity-40"
              onClick={() => tryDelete(v)} data-testid={`dm-tax-del-${v}`} aria-label={`Remove ${v}`}>
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );

  return (
    <SectionCard
      title="Spend taxonomy"
      description="Manage the classification model. Project spends (ATL, Brand, BTL, CX, CXX, Digital, Infrastructure) and Corporate spends (Management, Digital Support, Office) are grouped by spend type; corporate spends attribute to an entity. Removing a vertical that still has invoices forces a reassignment first — no record is ever orphaned."
      testId="dm-taxonomy-card"
    >
      <div className="space-y-5 p-4">
        {loading && !tax ? (
          <Skeleton className="h-56 w-full rounded-xl" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border p-4" data-testid="dm-tax-project">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Tags className="h-3.5 w-3.5" /> Project verticals
                </p>
                <VerticalChips list={tax?.project_verticals} kind="project" />
              </div>
              <div className="rounded-xl border p-4" data-testid="dm-tax-corporate">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Tags className="h-3.5 w-3.5" /> Corporate verticals
                </p>
                <VerticalChips list={tax?.corporate_verticals} kind="corporate" />
              </div>
            </div>

            <div className="rounded-xl border p-4" data-testid="dm-tax-entities">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Database className="h-3.5 w-3.5" /> Corporate entities
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {(tax?.corporate_entities || []).map((en) => (
                  <span key={en} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs" data-testid={`dm-tax-entity-${en}`}>
                    <span className="font-medium">{en}</span>
                    {canEdit && (
                      <button type="button" disabled={busy}
                        className="text-muted-foreground transition-colors duration-150 hover:text-[hsl(var(--danger))] disabled:opacity-40"
                        onClick={() => removeEntity(en)} data-testid={`dm-tax-entity-del-${en}`} aria-label={`Remove ${en}`}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <Input value={newEntity} onChange={(e) => setNewEntity(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEntity(); } }}
                      placeholder="Add entity…" className="h-8 w-[150px]" data-testid="dm-tax-entity-input" />
                    <Button size="sm" variant="outline" className="h-8" disabled={busy || !newEntity.trim()} onClick={addEntity} data-testid="dm-tax-entity-add">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {canEdit && (
              <div className="rounded-xl border border-primary/30 bg-[hsl(var(--primary)/0.04)] p-4" data-testid="dm-tax-add">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Add a vertical</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Name</Label>
                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Events" className="mt-1 h-9 w-[180px]" data-testid="dm-tax-new-name" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Spend type</Label>
                    <Select value={newType} onValueChange={setNewType}>
                      <SelectTrigger className="mt-1 h-9 w-[150px]" data-testid="dm-tax-new-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Project">Project</SelectItem>
                        <SelectItem value="Corporate">Corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Display label (optional)</Label>
                    <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Defaults to name" className="mt-1 h-9 w-[180px]" data-testid="dm-tax-new-label" />
                  </div>
                  <Button className="h-9" disabled={busy || !newName.trim()} onClick={addVertical} data-testid="dm-tax-add-btn">
                    <Plus className="mr-1.5 h-4 w-4" /> Add vertical
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reassign-before-delete dialog */}
      <Dialog open={!!reassign} onOpenChange={(o) => !o && !busy && setReassign(null)}>
        <DialogContent data-testid="dm-tax-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign before removing “{reassign?.name}”</DialogTitle>
            <DialogDescription>
              {reassign?.count} invoice(s) are still tagged <b>{reassign?.name}</b>. Choose a vertical to move them to — they’ll be re-tagged first so nothing is orphaned, then <b>{reassign?.name}</b> is removed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-muted-foreground">Reassign {reassign?.count} invoice(s) to</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger className="mt-1" data-testid="dm-tax-reassign-select"><SelectValue placeholder="Choose a vertical…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(reassign?.options || []).map((o) => <SelectItem key={o} value={o}>{labels[o] || o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" disabled={busy} onClick={() => setReassign(null)} data-testid="dm-tax-reassign-cancel">Cancel</Button>
            <Button disabled={busy || !reassignTo} onClick={() => tryDelete(reassign.name, reassignTo)}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]" data-testid="dm-tax-reassign-confirm">
              {busy ? "Reassigning…" : `Reassign & remove`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

/* -------------------------------------------------- payment status (bulk + excel) */
function PaymentStatusCard({ canEdit }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.paymentStatusTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "payment_status_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill the Payment Status column and re-upload");
    } catch (e) { toast.error("Could not download template"); }
    finally { setDownloading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { toast.error("Please choose an .xlsx workbook"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.importPaymentStatus(fd);
      setReport(res.report);
      toast.success(`Updated ${res.report.updated} invoice(s)`, {
        description: `${res.report.matched} matched · ${res.report.unmatched} unmatched · ${res.report.invalid} invalid`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment status import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const Stat = ({ label, value, accent }) => (
    <div className={`rounded-lg border p-3 ${accent || "bg-card"}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );

  return (
    <SectionCard
      title="Update payment status"
      description="Bulk-set invoice payment status (Paid / Partial / Unpaid). Download the template — each row carries its invoice id so a re-upload matches exactly — fill the Payment Status column and upload. Only payment status changes; invoices, leads and targets stay intact. You can also multi-select rows in the Spends tab below to set status inline."
      testId="dm-payment-card"
      action={canEdit && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={downloading} onClick={download} data-testid="dm-payment-template-btn">
            <Download className="mr-1.5 h-3.5 w-3.5" /> {downloading ? "Preparing…" : "Download template"}
          </Button>
          <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="dm-payment-upload-btn">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
            {busy ? "Uploading…" : "Upload filled .xlsx"}
          </Button>
        </div>
      )}
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="dm-payment-file-input" />
      <div className="p-4">
        {!report ? (
          <div className="flex items-start gap-2.5 rounded-lg border border-dashed bg-muted/30 p-3.5 text-sm text-muted-foreground" data-testid="dm-payment-empty">
            <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
            <span>Download → set each row to Paid / Partial / Unpaid → upload. Rows are matched by their hidden id, so nothing else is affected.</span>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dm-payment-report">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Rows read" value={report.rows} />
              <Stat label="Matched" value={report.matched} />
              <Stat label="Updated" value={report.updated} accent="bg-[hsl(var(--success)/0.08)]" />
              <Stat label="Unmatched / invalid" value={`${report.unmatched} / ${report.invalid}`} accent={(report.unmatched || report.invalid) ? "bg-[hsl(var(--warning)/0.08)]" : "bg-card"} />
            </div>
            {(report.unmatched > 0 || report.invalid > 0) && (
              <div className="flex items-start gap-2.5 rounded-lg border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] p-3 text-xs" data-testid="dm-payment-warn">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
                <span>
                  {report.unmatched > 0 && <>{report.unmatched} row id(s) didn’t match any invoice. </>}
                  {report.invalid > 0 && <>{report.invalid} row(s) had a status other than Paid / Partial / Unpaid and were skipped.</>}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/* -------- Guided, non-destructive invoice upload with vertical mapping -------- */
function AppendInvoicesPanel({ canEdit, onImported }) {
  const { money } = useCurrency();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [mappings, setMappings] = useState({}); // { label(lower): canonical }
  const [report, setReport] = useState(null);

  const doAnalyze = async (file) => {
    if (!file) return;
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.analyzeInvoices(fd);
      // pre-seed mappings from any already-known mapping
      const seed = {};
      [...(res.unknown_verticals || []), ...(res.unknown_sub_verticals || [])].forEach((lbl) => {
        const k = lbl.toLowerCase();
        if (res.existing_map && res.existing_map[k]) seed[k] = res.existing_map[k];
      });
      setMappings(seed);
      setAnalysis(res);
      setFileObj(file);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read workbook");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const close = () => { setAnalysis(null); setFileObj(null); setMappings({}); };

  const unknownVerts = analysis?.unknown_verticals || [];
  const unknownSubs = analysis?.unknown_sub_verticals || [];
  const canonical = analysis?.canonical_verticals || [];
  // every unknown VERTICAL must be mapped; sub-verticals are optional
  const allVertsMapped = unknownVerts.every((v) => mappings[v.toLowerCase()]);

  const setMap = (label, canonicalTag) =>
    setMappings((m) => ({ ...m, [label.toLowerCase()]: canonicalTag }));

  const doImport = async () => {
    if (!fileObj) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("mappings", JSON.stringify(mappings));
      const res = await api.appendInvoices(fd);
      setReport(res.report);
      toast.success(`Added ${res.report.inserted} invoice(s)`, {
        description: res.report.skipped_duplicates
          ? `${res.report.skipped_duplicates} duplicate(s) skipped.`
          : "Mapped to your tags and added to the dashboard.",
      });
      close();
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setCommitting(false); }
  };

  const MapRow = ({ label, kind }) => (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2" data-testid={`vmap-row-${label}`}>
      <Badge variant={kind === "vertical" ? "outline" : "secondary"} className="text-[10px]">{kind === "vertical" ? "Vertical" : "Sub-vertical"}</Badge>
      <span className="min-w-0 flex-1 truncate text-sm font-medium" title={label}>{label}</span>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
      <Select value={mappings[label.toLowerCase()] || ""} onValueChange={(v) => setMap(label, v)}>
        <SelectTrigger className="h-8 w-[190px] text-xs" data-testid={`vmap-select-${label}`}>
          <SelectValue placeholder="Map to a vertical…" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {canonical.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <SectionCard title="Upload & map invoices" description="Add invoices from an .xlsx WITHOUT replacing anything. If the sheet has a vertical or sub-vertical we don't recognise, you'll map it to one of your dashboard tags — and we'll remember it next time." testId="dm-append-card">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => doAnalyze(e.target.files?.[0])} data-testid="dm-append-input" />
          <Button disabled={!canEdit || busy} onClick={() => fileRef.current?.click()} data-testid="dm-append-btn">
            <Upload className="mr-2 h-4 w-4" /> {busy ? "Reading…" : "Choose .xlsx & map"}
          </Button>
          {!canEdit && <span className="text-xs text-muted-foreground">Only an Admin can upload &amp; map invoices.</span>}
          <span className="text-xs text-muted-foreground">Adds to existing invoices — duplicates are skipped automatically.</span>
        </div>
        {report && (
          <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-3 text-sm" data-testid="dm-append-report">
            <div className="mb-2 flex items-center gap-2 font-medium text-[hsl(var(--success))]">
              <CheckCircle2 className="h-4 w-4" /> Invoices added — main dashboard updated
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-4">
              <span>Added: <b className="tabular-nums">{report.inserted}</b></span>
              <span>Duplicates skipped: <b className="tabular-nums">{report.skipped_duplicates}</b></span>
              <span>Total: <b className="tabular-nums">₹{(report.total_cr).toFixed(2)} Cr</b></span>
              <span>Verticals mapped: <b className="tabular-nums">{report.vertical_resolved}</b></span>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!analysis} onOpenChange={(o) => !o && !committing && close()}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="dm-append-dialog">
          <DialogHeader>
            <DialogTitle>Map &amp; add {analysis?.rows} invoice(s)</DialogTitle>
            <DialogDescription>
              Nothing is replaced — these rows are <b className="text-foreground">added</b>. Map any unrecognised
              vertical/sub-vertical to a dashboard tag, then confirm.
            </DialogDescription>
          </DialogHeader>

          {analysis && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="dm-append-summary">
                <DiffTile label="Invoices to add" before={0} after={analysis.rows} />
                <DiffTile label="Total value" before={money(0)} after={money(analysis.total)} />
                <DiffTile label="Needs mapping" before={0} after={unknownVerts.length + unknownSubs.length} kept={unknownVerts.length + unknownSubs.length === 0} />
              </div>

              {(unknownVerts.length > 0 || unknownSubs.length > 0) ? (
                <div className="rounded-lg border bg-card p-3" data-testid="dm-append-mapping">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground">
                    <Tags className="h-3.5 w-3.5" /> Map these to a dashboard vertical
                  </p>
                  <div className="space-y-2">
                    {unknownVerts.map((v) => <MapRow key={`v-${v}`} label={v} kind="vertical" />)}
                    {unknownSubs.map((s) => <MapRow key={`s-${s}`} label={s} kind="sub" />)}
                  </div>
                  {!allVertsMapped && (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-[hsl(var(--warning))]">
                      <AlertTriangle className="h-3.5 w-3.5" /> Map every unrecognised vertical to continue. Sub-verticals are optional.
                    </p>
                  )}
                </div>
              ) : (
                <p className="rounded-lg border bg-[hsl(var(--success)/0.06)] px-3 py-2 text-xs text-[hsl(var(--success))]" data-testid="dm-append-allknown">
                  All verticals in this sheet are already recognised — no mapping needed.
                </p>
              )}

              <div>
                <p className="mb-1.5 text-xs font-medium">Sample of parsed invoices (first {analysis.sample?.length || 0})</p>
                <div className="max-h-52 overflow-auto rounded-lg border" data-testid="dm-append-sample">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[hsl(var(--surface-2))] text-muted-foreground">
                      <tr>{["Date", "Project", "Vertical", "Platform", "Activity", "Vendor", "Amount"].map((h) => <th key={h} className={`px-2 py-1.5 ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y">
                      {(analysis.sample || []).map((s, i) => (
                        <tr key={i}>
                          <td className="whitespace-nowrap px-2 py-1">{s.date || "—"}</td>
                          <td className="px-2 py-1">{s.project || "—"}</td>
                          <td className="px-2 py-1">{s.vertical || <span className="text-[hsl(var(--warning))]">unmapped</span>}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.platform || "—"}</td>
                          <td className="max-w-[150px] truncate px-2 py-1 text-muted-foreground" title={s.activity}>{s.activity || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground">{s.vendor || "—"}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{money(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={committing} data-testid="dm-append-cancel">Cancel</Button>
            <Button onClick={doImport} disabled={committing || !allVertsMapped} data-testid="dm-append-confirm">
              {committing ? "Adding…" : `Add ${analysis?.rows || ""} invoice(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

export function DataManager() {
  const [summary, setSummary] = useState(null);
  const [lists, setLists] = useState(null);
  const [role, setRole] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadSummary = useCallback(() => { api.dataSummary().then(setSummary); }, []);
  const loadLists = useCallback(() => { api.lists().then(setLists); }, []);
  useEffect(() => {
    loadSummary();
    loadLists();
    api.me().then((u) => setRole(u.role)).catch(() => {});
  }, [loadSummary, loadLists]);
  const canEdit = ["admin", "leader", "manager"].includes(role);
  const canDelete = ["admin", "leader"].includes(role);
  const isAdmin = role === "admin";
  const refresh = () => { loadSummary(); loadLists(); };
  const afterDataReplaced = () => { refresh(); setReloadToken((t) => t + 1); };

  const doExport = async () => {
    try {
      const res = await api.dataExport();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "sumadhura_data.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel export downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid="data-manager">
      <SummaryBar summary={summary} />
      <ImportPanel onImported={afterDataReplaced} canEdit={canDelete} />
      {isAdmin && <AppendInvoicesPanel canEdit={isAdmin} onImported={afterDataReplaced} />}
      <BudgetAllocationImport canEdit={canDelete} />
      <TaxonomyManager canEdit={canDelete} onChanged={afterDataReplaced} />
      <PaymentStatusCard canEdit={canEdit} />
      <SectionCard title="Edit data" description="Full add / edit / delete access. Select rows to bulk-retag or bulk-delete, merge duplicate vendors, and view each row's change history." testId="dm-edit-card"
        action={<Button variant="outline" size="sm" onClick={doExport} data-testid="dm-export-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> Export .xlsx</Button>}>
        <div className="p-4">
          <Tabs defaultValue="spends">
            <TabsList data-testid="dm-tabs">
              <TabsTrigger value="spends" data-testid="dm-tab-spends"><Database className="mr-1.5 h-3.5 w-3.5" /> Spends</TabsTrigger>
              <TabsTrigger value="funnel" data-testid="dm-tab-funnel"><TrendingUp className="mr-1.5 h-3.5 w-3.5" /> Leads / Funnel</TabsTrigger>
              <TabsTrigger value="audit" data-testid="dm-tab-audit"><ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Tag Audit</TabsTrigger>
            </TabsList>
            <TabsContent value="spends" className="mt-4"><SpendsPanel lists={lists} canEdit={canEdit} canDelete={canDelete} onChanged={refresh} reloadToken={reloadToken} /></TabsContent>
            <TabsContent value="funnel" className="mt-4"><FunnelPanel lists={lists} canEdit={canEdit} canDelete={canDelete} onChanged={refresh} /></TabsContent>
            <TabsContent value="audit" className="mt-4"><TagAuditPanel reloadToken={reloadToken} /></TabsContent>
          </Tabs>
        </div>
      </SectionCard>
      {canDelete && <DangerZone summary={summary} onPurged={afterDataReplaced} />}
    </div>
  );
}
